import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, FileBarChart } from "lucide-react";
import { differenceInDays, format } from "date-fns";

interface Dsr {
  id: string;
  request_type: string;
  status: string;
  received_at: string;
  due_at: string;
  extended_due_at: string | null;
}

interface Incident {
  id: string;
  severity: string;
  status: string;
  detected_at: string;
  anpd_notification_required: boolean;
  anpd_notified_at: string | null;
}

const STATUS_OPEN = ["received", "under_review", "in_progress", "extended"];

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface AcceptanceMetrics {
  patientsWithConsent: number;
  totalPatients: number;
  usersWithCurrentLegal: number;
  totalUsers: number;
  currentDocs: { slug: string; version: string }[];
}

export function ReportsTab() {
  const [dsrs, setDsrs] = useState<Dsr[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [acceptance, setAcceptance] = useState<AcceptanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);

      // Fetch DSRs and incidents
      const [dsrRes, incRes] = await Promise.all([
        supabase.from("data_subject_requests").select("id, request_type, status, received_at, due_at, extended_due_at"),
        supabase.from("security_incidents").select("id, severity, status, detected_at, anpd_notification_required, anpd_notified_at"),
      ]);
      setDsrs((dsrRes.data as Dsr[]) ?? []);
      setIncidents((incRes.data as Incident[]) ?? []);

      // Patient consent coverage: % of patients with at least one active consent
      const [patientsRes, consentsRes] = await Promise.all([
        supabase.from("patients").select("id"),
        supabase.from("patient_consents").select("patient_id").is("revoked_at", null),
      ]);
      const totalPatients = patientsRes.data?.length ?? 0;
      const patientsWithConsent = new Set(
        (consentsRes.data ?? []).map((c) => (c as { patient_id: string }).patient_id),
      ).size;

      // Legal acceptance coverage (Terms + Privacy): % of users that accepted
      // the *currently effective* version of every published legal document.
      const { data: docsData } = await supabase
        .from("legal_documents")
        .select("id, slug, version, effective_at, superseded_at")
        .is("superseded_at", null);
      const currentDocs = (docsData ?? []) as Array<{
        id: string; slug: string; version: string;
      }>;
      const currentDocIds = currentDocs.map((d) => d.id);

      const { data: profilesData } = await supabase.from("profiles").select("user_id");
      const totalUsers = profilesData?.length ?? 0;

      let usersWithCurrentLegal = 0;
      if (currentDocIds.length > 0 && totalUsers > 0) {
        const { data: accData } = await supabase
          .from("user_legal_acceptances")
          .select("user_id, document_id")
          .in("document_id", currentDocIds);
        // Count users that accepted ALL current documents
        const perUser = new Map<string, Set<string>>();
        for (const row of accData ?? []) {
          const r = row as { user_id: string; document_id: string };
          if (!perUser.has(r.user_id)) perUser.set(r.user_id, new Set());
          perUser.get(r.user_id)!.add(r.document_id);
        }
        for (const accepted of perUser.values()) {
          if (currentDocIds.every((id) => accepted.has(id))) usersWithCurrentLegal++;
        }
      }

      setAcceptance({
        patientsWithConsent,
        totalPatients,
        usersWithCurrentLegal,
        totalUsers,
        currentDocs: currentDocs.map((d) => ({ slug: d.slug, version: d.version })),
      });
      setLoading(false);
    })();
  }, []);

  const dsrMetrics = useMemo(() => {
    const now = new Date();
    const total = dsrs.length;
    const fulfilled = dsrs.filter((d) => d.status === "fulfilled");
    const open = dsrs.filter((d) => STATUS_OPEN.includes(d.status));
    const overdue = open.filter((d) => new Date(d.extended_due_at ?? d.due_at) < now);
    const onTime = fulfilled.filter((d) => true).length; // proxy: all fulfilled count
    const slaPct = total > 0 ? Math.round((fulfilled.length / total) * 100) : 0;

    const byType: Record<string, number> = {};
    const byStatus: Record<string, number> = {};
    for (const d of dsrs) {
      byType[d.request_type] = (byType[d.request_type] ?? 0) + 1;
      byStatus[d.status] = (byStatus[d.status] ?? 0) + 1;
    }
    return { total, fulfilled: fulfilled.length, open: open.length, overdue: overdue.length, onTime, slaPct, byType, byStatus };
  }, [dsrs]);

  const incidentMetrics = useMemo(() => {
    const bySeverity: Record<string, number> = { P1: 0, P2: 0, P3: 0, P4: 0 };
    for (const i of incidents) bySeverity[i.severity] = (bySeverity[i.severity] ?? 0) + 1;
    const open = incidents.filter((i) => i.status !== "closed").length;
    const anpdPending = incidents.filter((i) => i.anpd_notification_required && !i.anpd_notified_at).length;
    return { total: incidents.length, open, anpdPending, bySeverity };
  }, [incidents]);

  const exportDsrCsv = () => {
    downloadCsv(
      `dsr-${format(new Date(), "yyyy-MM-dd")}.csv`,
      dsrs.map((d) => ({
        id: d.id,
        type: d.request_type,
        status: d.status,
        received_at: d.received_at,
        due_at: d.due_at,
        extended_due_at: d.extended_due_at ?? "",
        days_to_due: differenceInDays(new Date(d.extended_due_at ?? d.due_at), new Date()),
      })),
    );
  };

  const exportIncidentsCsv = () => {
    downloadCsv(
      `incidents-${format(new Date(), "yyyy-MM-dd")}.csv`,
      incidents.map((i) => ({
        id: i.id,
        severity: i.severity,
        status: i.status,
        detected_at: i.detected_at,
        anpd_required: i.anpd_notification_required ? "yes" : "no",
        anpd_notified_at: i.anpd_notified_at ?? "",
      })),
    );
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileBarChart className="w-4 h-4" /> Solicitações de titular (DSR)
          </CardTitle>
          <CardDescription>Visão geral de conformidade com prazos LGPD.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Total recebidas" value={dsrMetrics.total} />
            <Metric label="Concluídas" value={dsrMetrics.fulfilled} />
            <Metric label="Em aberto" value={dsrMetrics.open} />
            <Metric label="Atrasadas" value={dsrMetrics.overdue} tone={dsrMetrics.overdue > 0 ? "destructive" : undefined} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <BreakdownList title="Por tipo" data={dsrMetrics.byType} />
            <BreakdownList title="Por status" data={dsrMetrics.byStatus} />
          </div>
          <div className="flex items-center justify-between border-t pt-3">
            <p className="text-sm">
              Taxa de atendimento: <strong>{dsrMetrics.slaPct}%</strong>
            </p>
            <Button size="sm" variant="outline" onClick={exportDsrCsv}>
              <Download className="w-4 h-4 mr-2" /> Exportar CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Incidentes de segurança</CardTitle>
          <CardDescription>LGPD art. 48 — comunicação à ANPD.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Total" value={incidentMetrics.total} />
            <Metric label="Em aberto" value={incidentMetrics.open} />
            <Metric label="ANPD pendente" value={incidentMetrics.anpdPending} tone={incidentMetrics.anpdPending > 0 ? "destructive" : undefined} />
            <Metric label="P1 críticos" value={incidentMetrics.bySeverity.P1 ?? 0} />
          </div>
          <BreakdownList title="Por severidade" data={incidentMetrics.bySeverity} />
          <div className="flex justify-end border-t pt-3">
            <Button size="sm" variant="outline" onClick={exportIncidentsCsv}>
              <Download className="w-4 h-4 mr-2" /> Exportar CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cobertura de consentimentos de paciente</CardTitle>
          <CardDescription>
            Percentual de pacientes com pelo menos um consentimento LGPD vigente registrado pelo responsável.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {acceptance && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Metric label="Pacientes com consentimento vigente" value={acceptance.patientsWithConsent} />
              <Metric label="Total de pacientes" value={acceptance.totalPatients} />
              <Metric
                label="Cobertura"
                value={
                  acceptance.totalPatients > 0
                    ? Math.round((acceptance.patientsWithConsent / acceptance.totalPatients) * 100)
                    : 0
                }
                suffix="%"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Aceite dos documentos legais vigentes</CardTitle>
          <CardDescription>
            Percentual de usuários que aceitaram a versão atual de todos os documentos legais publicados (Termos, Privacidade, etc.).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {acceptance && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Metric label="Usuários com aceite vigente" value={acceptance.usersWithCurrentLegal} />
                <Metric label="Total de usuários" value={acceptance.totalUsers} />
                <Metric
                  label="Cobertura"
                  value={
                    acceptance.totalUsers > 0
                      ? Math.round((acceptance.usersWithCurrentLegal / acceptance.totalUsers) * 100)
                      : 0
                  }
                  suffix="%"
                  tone={
                    acceptance.totalUsers > 0 &&
                    acceptance.usersWithCurrentLegal / acceptance.totalUsers < 0.8
                      ? "warn"
                      : undefined
                  }
                />
              </div>
              {acceptance.currentDocs.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum documento legal publicado.</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Documentos vigentes considerados:{" "}
                  {acceptance.currentDocs.map((d) => `${d.slug} v${d.version}`).join(", ")}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ label, value, tone, suffix }: { label: string; value: number; tone?: "destructive" | "warn"; suffix?: string }) {
  const color = tone === "destructive" ? "text-destructive" : tone === "warn" ? "text-warning" : "text-foreground";
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>
        {value}
        {suffix ? <span className="text-base font-normal ml-0.5">{suffix}</span> : null}
      </p>
    </div>
  );
}

function BreakdownList({ title, data }: { title: string; data: Record<string, number> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-2">{title}</p>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sem dados.</p>
      ) : (
        <ul className="space-y-1">
          {entries.map(([k, v]) => (
            <li key={k} className="flex justify-between text-sm">
              <span className="capitalize">{k.replace(/_/g, " ")}</span>
              <span className="font-medium">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
